/**
 * Water for the expansion missions that stand on it (10 §2: 13 the bridge, 20 the barges, 36 the ford).
 * 13 and 36 use the belt's own barrier (`ArchetypeSpec.river`, water.ts) - its fords, its ice, the ravine that
 * ends on its south bank; 20 digs a canal of its own, north-south, because a barge's hull cannot be turned.
 */

import { HALF_PI } from '../../sim/math';
import type { PropDef, WaterBarrierDef } from '../../sim/types';
import { FLAG } from '../grid';
import { courseZ } from '../water';
import type { GoalCtx } from './types';

export { courseZ } from '../water';

export interface Reach {
  barrier: WaterBarrierDef;
  /** Where it is crossed, and the centre line and the two banks there (north bank first). */
  x: number;
  z: number;
  north: number;
  south: number;
}

/** The belt's barrier where it is crossed at `x`; null (and a re-roll) when the map has none. */
export function reachAt(g: GoalCtx, x: number): Reach | null {
  const barrier = g.map.waterBarriers[0];
  if (!barrier) {
    g.map.problems.push(`${g.ctx.spec.id}: the map has no water barrier`);
    return null;
  }
  const z = courseZ(barrier.course, x);
  const half = barrier.course.width / 2;
  return { barrier, x, z, north: z - half, south: z + half };
}

/** Let the barrier out to `width` metres where the mission needs the room (three piers under one span). */
export function widen(g: GoalCtx, barrier: WaterBarrierDef, width: number): void {
  if (barrier.course.width >= width) return;
  barrier.course.width = width;
  g.map.mask.line(barrier.course.points, width / 2 + 1.5, FLAG.water);
  for (const c of barrier.crossings) g.map.mask.circle(c, courseZ(barrier.course, c), width / 2 + 3, FLAG.keep);
}

/** The road bridge at a crossing: the one the theatre already built there, let out to the span, or a new one. */
export function roadBridge(g: GoalCtx, reach: Reach): PropDef {
  const length = reach.barrier.course.width + 3;
  const built = g.map.props.find((p) => p.kind === 'bridge' && Math.abs(p.x - reach.x) < 1);
  if (!built) return g.map.add('bridge', reach.x, reach.z, { yaw: HALF_PI, length });
  built.length = length;
  return built;
}

/**
 * Is a prop's centre under a bridge's deck? The deck runs north-south (yaw a quarter turn) and is as wide as the
 * crossing lane and a little more (5.4 m); a pier stands under its edge, 2.7 m off the centre line.
 */
export function underDeck(bridge: PropDef, prop: PropDef, reach = 2.9): boolean {
  return Math.abs(prop.x - bridge.x) <= reach && Math.abs(prop.z - bridge.z) <= (bridge.length ?? 0) / 2;
}
